import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'

/**
 * Keep app content clear of the status bar.
 *
 * iOS (WKWebView) always draws under the status bar; there it is handled purely
 * in CSS via `viewport-fit=cover` + `env(safe-area-inset-top)`. Android WebView
 * does not reliably populate those `env()` insets, so content slides under the
 * status bar and the top controls (the camera CLEAN/BRANDED toggle and close
 * button) become untappable. Telling the status bar not to overlay makes the OS
 * reserve that space, which is the mechanism that actually works on Android.
 *
 * GoGrov is dark only (Brian), so the icons are light on the charcoal chrome.
 * Wrapped in try/catch: a status-bar hiccup must never block app startup.
 */
export async function initStatusBar(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await StatusBar.setStyle({ style: Style.Dark })
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setOverlaysWebView({ overlay: false })
      await StatusBar.setBackgroundColor({ color: '#18181b' })
    }
  } catch {
    // Non-fatal: the app is fully usable without an explicitly styled status bar.
  }
}

/**
 * Follows the app theme: dark icons on the light ground, light icons on dark.
 * Called whenever the theme changes, so the status bar never disappears into
 * the header behind it.
 */
export async function setStatusBarTheme(theme: 'light' | 'dark'): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light })
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: theme === 'dark' ? '#18181b' : '#f4f4ef' })
    }
  } catch {
    // Non-fatal.
  }
}
